import type { RouteGroup } from '@usealloy/daffy-to-frontend-dashboard-rollout';

/** Copy for one banner state. `body` only exists in the off state, which is a two-clause sentence. */
interface BannerStateCopy {
  /** Trailing clause, rendered between `lead` and the link. */
  body?: string;
  /** The bolded opening clause. */
  lead: string;
  /** Label for the "learn more" link that closes the sentence. */
  linkLabel: string;
}

interface BannerCopy {
  /** Shown in FE2.0, where the agent is already in the beta. */
  on: BannerStateCopy;
  /** Shown in legacy Daffy, where the agent has not opted in yet. */
  off: BannerStateCopy;
}

/**
 * Per-area banner copy, keyed by the matched route group's `featureFlag`.
 *
 * Deliberately duplicated from frontend-dashboard's copy of the same map rather than shared:
 * each app only ever renders one of the two states, and the two BetaPreferencesProviders are
 * already duplicated for the same reason. See FRONTEND-723's plan, Constraint 1.
 *
 * An area with no entry here renders no banner. Investigations is absent until design supplies
 * its copy (Open Question #4) — a banner with blank text is worse than none.
 */
export const BANNER_COPY: Partial<Record<RouteGroup['featureFlag'], BannerCopy>> = {
  'beta-routes-enabled': {
    off: {
      lead: 'New Application Review Experience is here',
      body: 'Open Beta is now available — try it out, or',
      linkLabel: 'learn more first',
    },
    on: {
      lead: "You're using our Applications Beta.",
      linkLabel: 'Learn more',
    },
  },
};

/**
 * Destination for the "learn more" link.
 */
export const LEARN_MORE_URL: string | undefined = 'https://help.alloy.com/hc/en-us/sections/54549209384219-Application-2-0-Beta';

/** Label for the Pendo-anchored feedback affordance. */
export const FEEDBACK_LABEL = 'Have Feedback?';

/**
 * Attribute Pendo targets to attach the feedback guide/survey to the banner. Pendo configuration
 * is owned outside this repo — the banner only exposes the anchor.
 */
export const FEEDBACK_PENDO_ANCHOR = 'beta-banner-feedback';

/** Height of the bar, from the Figma design. */
export const BANNER_HEIGHT = '48px';
